import { isProd } from "config/constant";
import useAuth from "hooks/useAuth";
import useInlineWebPush from "hooks/useInlineWebPush";
import { useLocalSocket } from "hooks/useLocalSocket";
import { useOnScreen } from "hooks/useOnScreen";
import React, { useContext, useEffect, useReducer, useRef } from "react";
import { useDispatch } from "react-redux";
import { loadState, saveState } from "util/localstorage";
import { isEmpty, lessThanSomeMinutesAgo } from "util/utils";

const localState = loadState();

export const AppContext = React.createContext();

export const LOCAL_STORAGE = "LOCAL_STORAGE";
const STORAGE_NAME = "btm";
const LIMIT_OFFSCREEN = "10"; // in mins

let ls;
let wd;

const initialState = localState
  ? { ...localState }
  : {
      isSignedIn: false,
      isLoading: true,
      errorState: { check: false },
      userData: undefined,
      token: undefined,
    };

const reducer = (state, action) => {
  let newState = { ...state };
  switch (action.type) {
    case "LOGIN":
      newState = { ...state, ...action.payload, isSignedIn: true };
      break;
    case "LOGOUT":
      newState = { ...initialState, ...action.payload, isSignedIn: false };
      break;

    case "UPDATE_USER_DATA":
      newState = {
        ...state,
        userData: { ...action.payload },
      };
      break;

    case "ERROR":
      newState = {
        ...state,
        errorState: { ...action.payload, check: true },
      };
      break;

    case "LOCAL_STORAGE":
      const stateFromLocalStorage = JSON.parse(
        localStorage.getItem(STORAGE_NAME) || "{}"
      );
      return stateFromLocalStorage;
    default:
      newState = state;
  }
  saveState(newState);
  return newState;
};

export default function Store(props) {
  const { token, isSignedIn, logout, login, profile, hydrateToken } = useAuth();
  const [state, dispatcher] = useReducer(reducer, initialState);
  const onScreen = useOnScreen();

  const lastOffScreenRef = useRef(new Date());

  const socket = useLocalSocket();

  const dispatch = (args) => {
    if (!args.payload || !args.type)
      throw new Error("Payload and type is required");
    dispatcher(args);
  };

  const rDispatch = useDispatch();

  const value = {
    store: {
      ...state,
      isLoading: profile?.status === "fetching" || profile?.status === "idle",
      token,
      isSignedIn,
      userData: profile?.data,
    },
    state,
    dispatch,
    localsocket: socket,
    login,
    logout,
    hydrateToken,
    rDispatch,
  };

  // logs user out if off scrren beyond limit
  useEffect(() => {
    if (isSignedIn) localStorage.setItem("tokenset", token);
    else if (!onScreen) {
      lastOffScreenRef.current = new Date();
    } else {
      const lastActiveScreen = lastOffScreenRef.current;
      const offBeyondLimit = !lessThanSomeMinutesAgo(
        lastActiveScreen,
        LIMIT_OFFSCREEN
      );

      if (offBeyondLimit) {
        socket &&
          isSignedIn &&
          socket.emit("LOGOUT-INACTIVE", {
            message: "Logged out for extended inactivity",
          });
      } else {
        lastOffScreenRef.current = new Date();
      }
    }
  }, [socket, onScreen, isSignedIn, token]);
  const initiated = useRef(false);
  useInlineWebPush(state?.userData, initiated.current);

  useEffect(() => {
    if (isSignedIn && state?.userData) initiated.current = true;
  }, [isSignedIn, state?.userData]);

  function listenForStorageChangesInOtherWindows(event) {
    if (event.key === "state" && event.oldValue !== event.newValue) {
      dispatcher && dispatcher({ type: LOCAL_STORAGE, payload: { ls } });
    }
  }

  // if this codes runs multiple time we add only one event listener
  // as long as the callback is singleton
  wd?.addEventListener("storage", listenForStorageChangesInOtherWindows, {
    once: true,
  });

  useEffect(() => {
    wd = window;
    ls = window.localStorage;
    try {
      // this runs only once
      // when browser reloads, get state from localstorage
      // localstorage is always kept up to date with last state before reload
      if (isEmpty(state.userData)) {
        dispatch({
          type: LOCAL_STORAGE,
          payload: { ls },
        });
      }
    } catch (error) {
      dispatch({
        type: "ERROR",
        payload: String(error),
      });
    }
    // eslint-disable-next-line
  }, [state?.userData]);

  useEffect(() => {
    if (socket && isSignedIn) {
      socket.connect();

      socket.on("LOGOUT-INACTIVE", () => {
        dispatch({
          type: "LOGOUT",
          payload: { isSignedIn: false },
        });
        logout();
      });
      socket.on("LOGOUT", () => {
        dispatch({
          type: "LOGOUT",
          payload: { isSignedIn: false, userData: null, token: null },
        });
        logout();
      });

      if (!isProd) {
        window.localSocket = socket;
      }
    } else {
      if (!state.isSignedIn) {
        window.localSocket = undefined;
      }
    }
  }, [socket, state.isSignedIn, isSignedIn, logout]);

  return (
    <AppContext.Provider value={value}>{props.children}</AppContext.Provider>
  );
}

export const useStore = () => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error("useStore must be used within an AppProvider");
  }
  return context;
};
